import React from 'react';
import {
  CaretLeft,
  CaretRight,
  ClockCounterClockwise,
  Eye,
} from '@phosphor-icons/react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ACTION_LABELS, SEVERITY_CONFIG } from './auditLogConstants';
import { formatDateTime } from '../../lib/utils';

const formatTimestamp = (ts) => formatDateTime(ts);

const formatPath = (path) => {
  if (!path) return '-';
  return path.length > 40 ? `${path.substring(0, 40)}...` : path;
};

const StatusBadge = ({ statusCode }) => {
  if (!statusCode) return null;
  const isError = statusCode >= 400;
  return (
    <Badge
      variant="outline"
      className={
        isError
          ? 'bg-red-50 text-red-700 border-red-200'
          : 'bg-green-50 text-green-700 border-green-200'
      }
    >
      {statusCode}
    </Badge>
  );
};

/**
 * Logs table with built-in pagination footer + empty/loading states.
 * Pure presentational — page state lives in the parent's `useAuditLogs`.
 */
const AuditLogTable = ({
  logs, total, loading, page, totalPages, onPageChange, onViewDetail,
}) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-0">
      <div className="flex justify-between items-center">
        <CardTitle className="text-lg">Activity Log</CardTitle>
        <span className="text-sm text-slate-500" data-testid="audit-count">
          Showing {logs.length} of {total.toLocaleString()} events
        </span>
      </div>
    </CardHeader>
    <CardContent className="p-0">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : logs.length === 0 ? (
        <div className="py-12 text-center" data-testid="audit-empty-state">
          <ClockCounterClockwise size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No audit logs found for the selected filters</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Timestamp</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead>Path</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[80px]">Severity</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => {
              const sev = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.unknown;
              return (
                <TableRow key={log.id} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs">{formatTimestamp(log.timestamp)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{log.user_email || '-'}</p>
                      {log.ip_address && <p className="text-xs text-slate-400">{log.ip_address}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{ACTION_LABELS[log.action] || log.action}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {log.resource_type ? <span className="capitalize">{log.resource_type}</span> : '-'}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{formatPath(log.path)}</TableCell>
                  <TableCell><StatusBadge statusCode={log.status_code} /></TableCell>
                  <TableCell><Badge className={sev.color}>{sev.label}</Badge></TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetail(log)}
                      data-testid={`view-log-${log.id}`}
                    >
                      <Eye size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          <p className="text-sm text-slate-500">Page {page + 1} of {totalPages}</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(0, page - 1))}
              disabled={page === 0}
              data-testid="audit-prev-page"
            >
              <CaretLeft size={16} className="mr-1" /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              data-testid="audit-next-page"
            >
              Next <CaretRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

export default AuditLogTable;
